import React, { useState } from 'react';
import { HelpCircle, Search, Heart, MessageCircle, Shield, Users, FileText, ChevronDown, ChevronUp } from 'lucide-react';

const Help = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "¿Cómo puedo crear mi cuenta?",
      answer: "Para crear tu cuenta, ve a la página principal y haz clic en 'Registrarse'. Completa el formulario con tu información personal y verifica tu correo electrónico."
    },
    {
      id: 2,
      question: "¿Es seguro usar SolterosMexico?",
      answer: "Sí, tu seguridad es nuestra prioridad. Utilizamos encriptación SSL, verificamos perfiles y tienes control total sobre tu información personal."
    },
    {
      id: 3,
      question: "¿Cómo puedo buscar pareja?",
      answer: "Usa los filtros en la sección 'Buscar Pareja' para encontrar personas según edad, ubicación, género y otros criterios que te interesen."
    },
    {
      id: 4,
      question: "¿Puedo eliminar mi cuenta?",
      answer: "Sí, puedes eliminar tu cuenta en cualquier momento desde la configuración de tu perfil. Todos tus datos se eliminarán permanentemente."
    },
    {
      id: 5,
      question: "¿Cómo funciona el sistema de mensajes?",
      answer: "Puedes enviar mensajes a cualquier usuario que te interese. Los mensajes son privados y solo visibles entre ambos usuarios."
    },
    {
      id: 6,
      question: "¿Qué hago si encuentro un perfil falso?",
      answer: "Reporta inmediatamente cualquier perfil sospechoso usando el botón 'Reportar' en el perfil del usuario. Nuestro equipo lo revisará."
    }
  ];

  const tips = [
    {
      icon: Search,
      title: "Usa filtros específicos",
      description: "Define bien qué buscas para encontrar mejores coincidencias."
    },
    {
      icon: Heart,
      title: "Sé auténtico",
      description: "Muestra tu verdadera personalidad en tu perfil."
    },
    {
      icon: MessageCircle,
      title: "Inicia conversaciones",
      description: "No esperes, toma la iniciativa y envía mensajes interesantes."
    },
    {
      icon: Shield,
      title: "Mantén tu privacidad",
      description: "No compartas información personal hasta conocer bien a la persona."
    }
  ];

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <HelpCircle className="h-12 w-12 text-primary" />
            <h1 className="text-4xl font-bold text-text">Centro de Ayuda</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Encuentra respuestas a tus preguntas y aprende a usar SolterosMexico de la mejor manera
          </p>
        </div>

        {/* Tips para conocer gente */}
        <div className="card mb-12">
          <h2 className="text-2xl font-bold text-text mb-6 flex items-center space-x-2">
            <Users className="h-6 w-6 text-primary" />
            <span>Tips para conocer gente</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text mb-1">{tip.title}</h3>
                    <p className="text-gray-600 text-sm">{tip.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cómo usar la plataforma */}
        <div className="card mb-12">
          <h2 className="text-2xl font-bold text-text mb-6 flex items-center space-x-2">
            <FileText className="h-6 w-6 text-primary" />
            <span>Cómo usar SolterosMexico</span>
          </h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-lg font-semibold text-text mb-2">1. Crea tu perfil</h3>
              <p className="text-gray-600">
                Completa tu perfil con información honesta y atractiva. Agrega fotos de calidad y escribe una descripción que refleje tu personalidad.
              </p>
            </div>
            
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-lg font-semibold text-text mb-2">2. Busca pareja</h3>
              <p className="text-gray-600">
                Usa los filtros de búsqueda para encontrar personas que coincidan con tus intereses y preferencias.
              </p>
            </div>
            
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-lg font-semibold text-text mb-2">3. Inicia contacto</h3>
              <p className="text-gray-600">
                Envía mensajes personalizados a las personas que te interesen. Sé respetuoso y original en tu acercamiento.
              </p>
            </div>
            
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-lg font-semibold text-text mb-2">4. Construye relaciones</h3>
              <p className="text-gray-600">
                Mantén conversaciones interesantes y conoce mejor a la persona antes de proponer un encuentro.
              </p>
            </div>
          </div>
        </div>

        {/* Preguntas frecuentes */}
        <div className="card">
          <h2 className="text-2xl font-bold text-text mb-6">Preguntas Frecuentes</h2>
          
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="font-medium text-text">{faq.question}</span>
                  {openFaq === faq.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {openFaq === faq.id && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contacto de soporte */}
        <div className="card mt-8 text-center">
          <h3 className="text-xl font-semibold text-text mb-4">¿No encontraste lo que buscabas?</h3>
          <p className="text-gray-600 mb-6">
            Nuestro equipo de soporte está aquí para ayudarte
          </p>
          <div className="flex justify-center">
            <p className="text-lg text-gray-700 font-medium">
              Email: ayuda@solterosmexico.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;
